import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { MainLayout } from '../components/MainLayout';
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert, 
  Key, 
  Smartphone, 
  Clock, 
  Monitor, 
  RefreshCw, 
  ChevronLeft, 
  ChevronRight, 
  Copy, 
  Check, 
  Eye, 
  EyeOff, 
  AlertTriangle, 
  CheckCircle2, 
  X, 
  Lock, 
  LogOut, 
  Download, 
  ExternalLink,
  Laptop
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import {
  getSessions,
  revokeSession,
  revokeAllOtherSessions,
  getTwoFactorStatus,
  beginTwoFactorSetup,
  confirmTwoFactorSetup,
  disableTwoFactor,
  regenerateRecoveryCodes,
  getRecoveryCodes,
  getAuditEvents,
} from '../api/security';

export default function Security() {
  const { user, logout, changeMasterPassword } = useAuth();
  
  // Data states
  const [sessions, setSessions] = useState([]);
  const [twoFactorStatus, setTwoFactorStatus] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditPage, setAuditPage] = useState(1);
  const [auditLoading, setAuditLoading] = useState(false);
  const AUDIT_PAGE_SIZE = 15;
  const [recoveryCodeRows, setRecoveryCodeRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // 2FA Setup Wizard Modal
  const [setupModalOpen, setSetupModalOpen] = useState(false);
  const [setupQrCode, setSetupQrCode] = useState('');
  const [setupSecret, setSetupSecret] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [confirming2FA, setConfirming2FA] = useState(false);
  const [setupError, setSetupError] = useState('');
  const [newRecoveryCodes, setNewRecoveryCodes] = useState([]);
  const [copiedSecret, setCopiedSecret] = useState(false);
  const [copiedCodes, setCopiedCodes] = useState(false);

  // Disable 2FA Modal
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disabling2FA, setDisabling2FA] = useState(false);

  // Change Password Modal
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPass, setShowCurrentPass] = useState(false);
  const [showNewPass, setShowNewPass] = useState(false);
  const [changingPass, setChangingPass] = useState(false);
  const [passwordError, setPasswordError] = useState('');

  // Auto-lock state (Deactivated by default)
  const [autoLockEnabled, setAutoLockEnabled] = useState(() => {
    return window.localStorage.getItem('cipher-vault-autolock-enabled') === 'true';
  });
  const [autoLockTimeout, setAutoLockTimeout] = useState(() => {
    return window.localStorage.getItem('cipher-vault-autolock') || '15';
  });

  const handleToggleAutoLockEnabled = () => {
    const next = !autoLockEnabled;
    setAutoLockEnabled(next);
    window.localStorage.setItem('cipher-vault-autolock-enabled', String(next));
    showToast(`Vault Inactivity Auto-Lock ${next ? 'activated' : 'deactivated'}.`);
  };

  // Toast feedback
  const [toast, setToast] = useState(null);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4500);
  };

  // Lock body scroll on mobile whenever any security modal is open
  useEffect(() => {
    const anyOpen = setupModalOpen || disableModalOpen || passwordModalOpen;
    if (anyOpen) {
      document.body.classList.add('mobile-modal-open');
    } else {
      document.body.classList.remove('mobile-modal-open');
    }
    return () => document.body.classList.remove('mobile-modal-open');
  }, [setupModalOpen, disableModalOpen, passwordModalOpen]);

  const loadAuditPage = useCallback(async (page) => {
    setAuditLoading(true);
    try {
      const result = await getAuditEvents(page, AUDIT_PAGE_SIZE);
      setAuditEvents(result.events || []);
      setAuditTotal(result.total || 0);
    } catch (e) {
      console.error('Failed to load audit events', e);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadSecurityData = useCallback(async () => {
    setLoading(true);
    try {
      const [sessionData, twoFactorData, recoveryData] = await Promise.all([
        getSessions().catch(() => []),
        getTwoFactorStatus().catch(() => ({ enabled: false })),
        getRecoveryCodes().catch(() => []),
      ]);

      setSessions(Array.isArray(sessionData) ? sessionData : []);
      setTwoFactorStatus(twoFactorData);
      setRecoveryCodeRows(Array.isArray(recoveryData) ? recoveryData : []);
    } catch (error) {
      showToast('Failed to load some security settings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSecurityData();
    loadAuditPage(1);
  }, [loadSecurityData, loadAuditPage]);

  useEffect(() => {
    loadAuditPage(auditPage);
  }, [auditPage, loadAuditPage]);

  // Session handlers
  const handleRevokeSession = async (id) => {
    try {
      await revokeSession(id);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      showToast('Session revoked successfully.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to revoke session', 'error');
    }
  };

  const handleLogoutAllOther = async () => {
    try {
      await revokeAllOtherSessions();
      setSessions((prev) => prev.filter((s) => s.is_current));
      showToast('All other sessions were terminated successfully.');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to revoke other sessions', 'error');
    }
  };

  // 2FA Setup Flow
  const handleOpenSetup2FA = async () => {
    setSetupError('');
    setVerificationCode('');
    setNewRecoveryCodes([]);
    setSetupModalOpen(true);
    try {
      const setup = await beginTwoFactorSetup();
      setSetupQrCode(setup.qrCodeDataUrl || '');
      setSetupSecret(setup.secret || '');
    } catch (err) {
      setSetupError(err.response?.data?.message || 'Failed to initiate 2FA setup');
    }
  };

  const handleConfirm2FA = async (e) => {
    e.preventDefault();
    if (!verificationCode.trim()) {
      setSetupError('Please enter the 6-digit authentication code.');
      return;
    }

    setConfirming2FA(true);
    setSetupError('');
    try {
      const result = await confirmTwoFactorSetup(verificationCode.trim());
      setTwoFactorStatus({ enabled: true, method: 'TOTP', status: 'ACTIVE' });
      setNewRecoveryCodes(result.recoveryCodes || []);
      showToast('Two-Factor Authentication is now enabled!');
      // Reload recovery codes
      const rec = await getRecoveryCodes().catch(() => []);
      setRecoveryCodeRows(rec);
    } catch (err) {
      setSetupError(err.response?.data?.message || 'Invalid code. Please try again.');
    } finally {
      setConfirming2FA(false);
    }
  };

  const handleDisable2FA = async () => {
    setDisabling2FA(true);
    try {
      await disableTwoFactor();
      setTwoFactorStatus({ enabled: false, method: 'TOTP', status: 'DISABLED' });
      setRecoveryCodeRows([]);
      setDisableModalOpen(false);
      showToast('Two-Factor Authentication has been disabled.', 'warning');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to disable 2FA', 'error');
    } finally {
      setDisabling2FA(false);
    }
  };

  const handleRegenerateCodes = async () => {
    try {
      const result = await regenerateRecoveryCodes();
      setNewRecoveryCodes(result.codes || []);
      setSetupModalOpen(true); // show them in modal
      setSetupQrCode(''); // only show codes view
      showToast('New recovery codes generated. Save them safely!');
      const rec = await getRecoveryCodes().catch(() => []);
      setRecoveryCodeRows(rec);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to regenerate codes', 'error');
    }
  };

  const handleCopySecret = () => {
    navigator.clipboard.writeText(setupSecret);
    setCopiedSecret(true);
    setTimeout(() => setCopiedSecret(false), 2000);
  };

  const handleCopyCodes = () => {
    navigator.clipboard.writeText(newRecoveryCodes.join('\n'));
    setCopiedCodes(true);
    setTimeout(() => setCopiedCodes(false), 2000);
  };

  const handleDownloadCodes = () => {
    const element = document.createElement('a');
    const file = new Blob([`CipherVault 2FA Backup Recovery Codes\nAccount: ${user?.email}\nDate: ${new Date().toISOString()}\n\n` + newRecoveryCodes.join('\n')], { type: 'text/plain' });
    element.href = URL.createObjectURL(file);
    element.download = 'ciphervault-recovery-codes.txt';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  // Master Password Change Flow
  const passwordStrength = useMemo(() => {
    if (!newPassword) return { score: 0, label: 'None', color: '#64748b' };
    let score = 0;
    if (newPassword.length >= 8) score += 1;
    if (newPassword.length >= 12) score += 1;
    if (/[A-Z]/.test(newPassword)) score += 1;
    if (/[0-9]/.test(newPassword)) score += 1;
    if (/[^A-Za-z0-9]/.test(newPassword)) score += 1;

    if (score <= 2) return { score, label: 'Weak', color: '#ef4444' };
    if (score <= 3) return { score, label: 'Moderate', color: '#f59e0b' };
    if (score <= 4) return { score, label: 'Strong', color: '#10b981' };
    return { score, label: 'Very Strong', color: '#059669' };
  }, [newPassword]);

  const handleChangeMasterPassword = async (e) => {
    e.preventDefault();
    setPasswordError('');

    if (!currentPassword) {
      setPasswordError('Please enter your current master password.');
      return;
    }
    if (newPassword.length < 8) {
      setPasswordError('New master password must be at least 8 characters long.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match. Please verify.');
      return;
    }
    if (currentPassword === newPassword) {
      setPasswordError('New password cannot be identical to your current password.');
      return;
    }

    setChangingPass(true);
    try {
      await changeMasterPassword(currentPassword, newPassword);
      setPasswordModalOpen(false);
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      showToast('Master password changed successfully! Your vault key was securely re-encrypted.');
      loadAuditPage(1);
    } catch (err) {
      setPasswordError(err.response?.data?.message || err.message || 'Failed to change master password');
    } finally {
      setChangingPass(false);
    }
  };

  // Auto-lock change
  const handleAutoLockChange = (e) => {
    const val = e.target.value;
    setAutoLockTimeout(val);
    window.localStorage.setItem('cipher-vault-autolock', val);
    showToast(`Vault auto-lock timeout updated to ${val} minutes.`);
  };

  // Format device icon
  const getDeviceIcon = (name) => {
    const lower = String(name || '').toLowerCase();
    if (lower.includes('mobile') || lower.includes('ios') || lower.includes('android')) {
      return <Smartphone size={20} />;
    }
    if (lower.includes('mac') || lower.includes('windows') || lower.includes('linux') || lower.includes('chrome')) {
      return <Laptop size={20} />;
    }
    return <Monitor size={20} />;
  };

  return (
    <MainLayout>
      {/* Toast Alert */}
      {toast && (
        <div className={`admin-toast admin-toast-${toast.type}`}>
          {toast.type === 'error' && <AlertTriangle size={18} />}
          {toast.type === 'warning' && <ShieldAlert size={18} />}
          {toast.type === 'success' && <CheckCircle2 size={18} />}
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="toast-close-btn" aria-label="Close message">
            <X size={15} />
          </button>
        </div>
      )}

      {/* Page Header - Full Bleed Edge-to-Edge */}
      <div className="security-header-pro notes-hero-banner">
        <div className="notes-hero-content">
          <div className="security-header-titles">
            <h1 className="security-page-title notes-hero-title">Security & Protection</h1>
            <p className="security-page-subtitle notes-hero-subtitle">
              Configure multi-factor authentication, manage your encryption key, active sessions, and compliance logs.
            </p>
          </div>
          <div className="security-header-actions">
            <button 
              className="btn-secondary notes-hero-btn-secondary" 
              onClick={() => { loadSecurityData(); loadAuditPage(auditPage); }} 
              disabled={loading}
              title="Refresh security status"
            >
              <RefreshCw size={15} className={loading ? 'rotate-anim' : ''} />
              <span>Refresh</span>
            </button>
          </div>
        </div>
      </div>

      <div className="security-page-container">

        {/* Top 4 Security Metrics */}
        <div className="security-metrics-grid">
          {/* 2FA Card */}
          <div className="security-stat-card glass-panel">
            <div className="stat-card-top">
              <div className="stat-icon-wrap" style={{ background: twoFactorStatus?.enabled ? 'rgba(16, 185, 129, 0.12)' : 'rgba(239, 68, 68, 0.12)', color: twoFactorStatus?.enabled ? '#10b981' : '#ef4444' }}>
                <Smartphone size={22} />
              </div>
              <span className={`security-status-badge ${twoFactorStatus?.enabled ? 'badge-active' : 'badge-inactive'}`}>
                <span className="dot" /> {twoFactorStatus?.enabled ? 'Protected' : 'Action Required'}
              </span>
            </div>
            <div className="stat-card-body">
              <span className="stat-label">Two-Factor Authentication</span>
              <strong className="stat-value">{twoFactorStatus?.enabled ? 'Enabled' : 'Disabled'}</strong>
              <span className="stat-subtext">
                {twoFactorStatus?.enabled ? 'TOTP Authenticator Active' : 'Setup 2FA for extra defense'}
              </span>
            </div>
          </div>

          {/* Master Password Card */}
          <div className="security-stat-card glass-panel">
            <div className="stat-card-top">
              <div className="stat-icon-wrap" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                <Key size={22} />
              </div>
              <span className="security-status-badge badge-active">
                <span className="dot" /> Argon2id
              </span>
            </div>
            <div className="stat-card-body">
              <span className="stat-label">Master Encryption Key</span>
              <strong className="stat-value">End-to-End</strong>
              <span className="stat-subtext">Zero-Knowledge Protected</span>
            </div>
          </div>

          {/* Active Sessions Card */}
          <div className="security-stat-card glass-panel">
            <div className="stat-card-top">
              <div className="stat-icon-wrap" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
                <Monitor size={22} />
              </div>
              <span className="security-status-badge badge-neutral">
                {sessions.length} {sessions.length === 1 ? 'Device' : 'Devices'}
              </span>
            </div>
            <div className="stat-card-body">
              <span className="stat-label">Active Sessions</span>
              <strong className="stat-value">{sessions.length} Online</strong>
              <span className="stat-subtext">All devices encrypted</span>
            </div>
          </div>

          {/* Auto-Lock Card */}
          <div className="security-stat-card glass-panel">
            <div className="stat-card-top">
              <div className="stat-icon-wrap" style={{ background: 'rgba(217, 119, 6, 0.12)', color: '#d97706' }}>
                <Clock size={22} />
              </div>
              <span className={`security-status-badge ${autoLockEnabled ? 'badge-success' : 'badge-neutral'}`}>
                {autoLockEnabled ? `${autoLockTimeout} min` : 'Deactive'}
              </span>
            </div>
            <div className="stat-card-body">
              <span className="stat-label">Vault Auto-Lock</span>
              <strong className="stat-value">{autoLockEnabled ? `${autoLockTimeout} min` : 'Off'}</strong>
              <span className="stat-subtext">{autoLockEnabled ? 'Locks on idle inactivity' : 'Auto-lock is deactivated'}</span>
            </div>
          </div>
        </div>

        {/* 2-Column Core Layout */}
        <div className="security-layout-grid">
          {/* Left Column: 2FA & Password */}
          <div className="security-col-main">
            {/* 2FA Protection Card */}
            <div className="security-card glass-panel">
              <div className="security-card-header">
                <div className="card-header-icon-badge" style={{ background: 'rgba(16, 185, 129, 0.12)', color: '#10b981' }}>
                  <Smartphone size={22} />
                </div>
                <div>
                  <h3 className="card-title">Two-Factor Authentication (2FA)</h3>
                  <p className="card-subtitle">
                    Add a second security layer. When enabled, signing in requires your master password plus a 6-digit TOTP code.
                  </p>
                </div>
              </div>

              <div className="security-card-content">
                {twoFactorStatus?.enabled ? (
                  <div className="twofa-active-box">
                    <div className="twofa-status-row">
                      <div className="twofa-icon-indicator active">
                        <CheckCircle2 size={24} />
                      </div>
                      <div>
                        <strong>Two-Factor Authentication is currently active</strong>
                        <p>Your vault is secured with Time-Based One-Time Passwords (TOTP) and offline recovery backup codes.</p>
                      </div>
                    </div>

                    <div className="twofa-action-row">
                      <button className="btn-secondary" onClick={handleRegenerateCodes}>
                        <RefreshCw size={14} /> Regenerate Recovery Codes
                      </button>
                      <button className="btn-danger-outline" onClick={() => setDisableModalOpen(true)}>
                        Disable 2FA
                      </button>
                    </div>

                    {/* Stored Recovery Codes list */}
                    <div className="stored-recovery-section">
                      <div className="recovery-header">
                        <strong>Stored Recovery Codes ({recoveryCodeRows.length})</strong>
                        <span className="text-muted">Use when your authenticator device is unavailable</span>
                      </div>
                      {recoveryCodeRows.length > 0 ? (
                        <div className="recovery-code-pill-grid">
                          {recoveryCodeRows.map((r, idx) => (
                            <div key={r.id || idx} className={`recovery-code-pill ${r.used_at ? 'used' : 'active'}`}>
                              <span>Code #{idx + 1}</span>
                              <small>{r.used_at ? 'Used' : 'Available'}</small>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-muted" style={{ fontSize: '13px' }}>Click "Regenerate Recovery Codes" to create emergency backup codes.</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="twofa-inactive-box">
                    <div className="twofa-warning-alert">
                      <AlertTriangle size={20} className="alert-icon" />
                      <div>
                        <strong>2FA is not enabled on this account</strong>
                        <p>Protect your passwords from unauthorized access if your master password is ever exposed.</p>
                      </div>
                    </div>
                    <button className="btn-primary btn-lg" onClick={handleOpenSetup2FA}>
                      <Smartphone size={16} /> Set Up Two-Factor Authentication
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Master Password Card */}
            <div className="security-card glass-panel">
              <div className="security-card-header">
                <div className="card-header-icon-badge" style={{ background: 'rgba(59, 130, 246, 0.12)', color: '#3b82f6' }}>
                  <Key size={22} />
                </div>
                <div>
                  <h3 className="card-title">Master Password</h3>
                  <p className="card-subtitle">
                    The single key that encrypts and unlocks all your vault data. CipherVault never stores this password.
                  </p>
                </div>
              </div>

              <div className="security-card-content">
                <div className="master-password-info-box">
                  <div className="mp-info-left">
                    <div className="mp-shield-icon">
                      <Shield size={20} />
                    </div>
                    <div>
                      <strong>Zero-Knowledge Cryptography</strong>
                      <p>
                        Your master password derives a 256-bit encryption key on your device. Changing it will safely re-wrap your vault key without data loss.
                      </p>
                    </div>
                  </div>
                  <button className="btn-secondary" onClick={() => setPasswordModalOpen(true)}>
                    <Key size={14} /> Change Master Password
                  </button>
                </div>
              </div>
            </div>

            {/* Auto-Lock & Timeout Settings */}
           
          </div>

          {/* Right Column: Sessions, Emergency & Audit */}
          <div className="security-col-side">
            {/* Active Sessions Card */}
            <div className="security-card glass-panel">
              <div className="security-card-header">
                <div className="card-header-icon-badge" style={{ background: 'rgba(139, 92, 246, 0.12)', color: '#8b5cf6' }}>
                  <Monitor size={22} />
                </div>
                <div>
                  <h3 className="card-title">Active Devices & Sessions</h3>
                  <p className="card-subtitle">Devices currently logged into your account.</p>
                </div>
              </div>

              <div className="security-card-content">
                <div className="sessions-feed-list">
                  {sessions.map((s) => (
                    <div key={s.id} className={`session-feed-item ${s.is_current ? 'is-current' : ''}`}>
                      <div className="session-feed-icon">
                        {getDeviceIcon(s.device_name)}
                      </div>
                      <div className="session-feed-info">
                        <div className="session-feed-name-row">
                          <strong>{s.device_name || 'Web Browser'}</strong>
                          {s.is_current && <span className="current-pill">This Device</span>}
                        </div>
                        <span className="session-feed-meta">
                          {s.ip_address} • {new Date(s.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {!s.is_current && (
                        <button 
                          className="btn-revoke-session" 
                          onClick={() => handleRevokeSession(s.id)}
                          title="Revoke session"
                        >
                          Revoke
                        </button>
                      )}
                    </div>
                  ))}

                  {sessions.length === 0 && (
                    <p className="text-muted" style={{ textAlign: 'center', padding: '16px' }}>No sessions found.</p>
                  )}
                </div>

                {sessions.length > 1 && (
                  <div style={{ marginTop: '14px', borderTop: '1px solid var(--border-color)', paddingTop: '12px' }}>
                    <button className="btn-danger-outline btn-sm" style={{ width: '100%' }} onClick={handleLogoutAllOther}>
                      <LogOut size={14} /> Revoke All Other Sessions
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Emergency Panic Card */}
            <div className="security-card glass-panel panic-card">
              <div className="security-card-header">
                <div className="card-header-icon-badge panic-icon">
                  <ShieldAlert size={22} />
                </div>
                <div>
                  <h3 className="card-title" style={{ color: 'var(--danger)' }}>Emergency Panic Action</h3>
                  <p className="card-subtitle">Suspect account compromise? Immediately invalidate all tokens.</p>
                </div>
              </div>

              <div className="security-card-content">
                <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                  <button className="btn-danger" style={{ flex: 1 }} onClick={handleLogoutAllOther}>
                    Revoke Everywhere
                  </button>
                  <button className="btn-secondary" style={{ flex: 1 }} onClick={logout}>
                    Lock Vault Now
                  </button>
                </div>
              </div>
            </div>
            <div className="security-card glass-panel">
              <div className="security-card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <div className="card-header-icon-badge" style={{ background: 'rgba(217, 119, 6, 0.12)', color: '#d97706' }}>
                    <Clock size={22} />
                  </div>
                  <div>
                    <h3 className="card-title">Vault Timeout & Auto-Lock</h3>
                    <p className="card-subtitle">
                      Automatically close and lock your vault session when your browser is idle to protect against local snooping.
                    </p>
                  </div>
                </div>

                {/* Active / Deactive Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                  <span style={{
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    color: autoLockEnabled ? '#10b981' : 'var(--text-secondary)',
                    textTransform: 'uppercase'
                  }}>
                    {autoLockEnabled ? 'Active' : 'Deactive'}
                  </span>
                  <label style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={autoLockEnabled}
                      onChange={handleToggleAutoLockEnabled}
                      style={{ display: 'none' }}
                    />
                    <div style={{
                      width: 44,
                      height: 24,
                      borderRadius: 14,
                      background: autoLockEnabled ? '#10b981' : 'rgba(255, 255, 255, 0.16)',
                      position: 'relative',
                      transition: 'all 0.25s ease',
                      boxShadow: autoLockEnabled ? '0 2px 10px rgba(16, 185, 129, 0.35)' : 'none'
                    }}>
                      <div style={{
                        width: 18,
                        height: 18,
                        borderRadius: '50%',
                        background: '#ffffff',
                        position: 'absolute',
                        top: 3,
                        left: autoLockEnabled ? 23 : 3,
                        transition: 'left 0.25s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)'
                      }} />
                    </div>
                  </label>
                </div>
              </div>

              <div className="security-card-content" style={{ opacity: autoLockEnabled ? 1 : 0.55, pointerEvents: autoLockEnabled ? 'auto' : 'none', transition: 'opacity 0.2s ease' }}>
                <div className="autolock-select-row">
                  <label htmlFor="autolock-select">Auto-Lock Inactivity Period:</label>
                  <select 
                    id="autolock-select"
                    value={autoLockTimeout} 
                    onChange={handleAutoLockChange}
                    className="admin-select"
                    disabled={!autoLockEnabled}
                  >
                    <option value="1">1 minute (Strict)</option>
                    <option value="5">5 minutes</option>
                    <option value="15">15 minutes (Recommended)</option>
                    <option value="30">30 minutes</option>
                    <option value="60">1 hour</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Audit Trail Section (Full Width) */}
        <div className="security-card glass-panel" style={{ marginTop: '12px' }}>
          <div className="security-card-header">
            <div className="card-header-icon-badge" style={{ background: 'rgba(15, 118, 110, 0.12)', color: '#0f766e' }}>
              <RefreshCw size={22} />
            </div>
            <div>
              <h3 className="card-title">Security Activity & Audit Trail</h3>
              <p className="card-subtitle">Real-time tamper-evident log of authentications, policy adjustments, and security changes.</p>
            </div>
          </div>

          <div className="security-card-content">
            {auditLoading ? (
              <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
                <RefreshCw size={24} className="rotate-anim" style={{ margin: '0 auto 8px auto' }} />
                <p>Loading security audit logs...</p>
              </div>
            ) : auditEvents.length === 0 ? (
              <p className="text-muted" style={{ padding: '24px', textAlign: 'center' }}>No recent audit events recorded.</p>
            ) : (
              <div className="audit-events-table-wrap">
                <table className="audit-events-table">
                  <thead>
                    <tr>
                      <th>Event Action</th>
                      <th>Source IP</th>
                      <th>Timestamp</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditEvents.map((ev) => (
                      <tr key={ev.id}>
                        <td>
                          <span className="audit-action-chip">
                            {ev.action}
                          </span>
                        </td>
                        <td>
                          <span className="audit-ip">{ev.ip_address || '127.0.0.1'}</span>
                        </td>
                        <td>
                          <span className="audit-date">{new Date(ev.created_at).toLocaleString()}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination Controls */}
            {auditTotal > AUDIT_PAGE_SIZE && (
              <div className="audit-pagination-bar">
                <span className="audit-pagination-info">
                  Showing {((auditPage - 1) * AUDIT_PAGE_SIZE) + 1}–{Math.min(auditPage * AUDIT_PAGE_SIZE, auditTotal)} of {auditTotal} events
                </span>
                <div className="audit-pagination-buttons">
                  <button
                    onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                    disabled={auditPage === 1 || auditLoading}
                    className="btn-secondary btn-sm"
                  >
                    <ChevronLeft size={14} /> Previous
                  </button>
                  <span className="page-indicator">Page {auditPage} of {Math.ceil(auditTotal / AUDIT_PAGE_SIZE)}</span>
                  <button
                    onClick={() => setAuditPage((p) => p + 1)}
                    disabled={auditPage >= Math.ceil(auditTotal / AUDIT_PAGE_SIZE) || auditLoading}
                    className="btn-secondary btn-sm"
                  >
                    Next <ChevronRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ─── 2FA Setup Modal Wizard ─── */}
        {setupModalOpen && (
          <div className="modal-backdrop" onClick={() => setSetupModalOpen(false)}>
            <div className="modal-card glass-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
              <div className="modal-header">
                <div className="modal-header-icon">
                  <Smartphone size={20} />
                </div>
                <div>
                  <h3>{newRecoveryCodes.length > 0 ? 'Save Recovery Codes' : 'Set Up Two-Factor Authentication'}</h3>
                  <p>{newRecoveryCodes.length > 0 ? 'Store these emergency codes in a safe place' : 'Protect your vault with an authenticator app'}</p>
                </div>
                <button className="modal-close" onClick={() => setSetupModalOpen(false)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body-content">
                {setupError && (
                  <div className="admin-toast admin-toast-error" style={{ marginBottom: '12px' }}>
                    <AlertTriangle size={16} />
                    <span>{setupError}</span>
                  </div>
                )}

                {/* Wizard: Step 1 & 2 (QR Code and Verification) */}
                {newRecoveryCodes.length === 0 && (
                  <form onSubmit={handleConfirm2FA} className="twofa-wizard-form">
                    <div className="wizard-step">
                      <span className="step-number">1</span>
                      <div className="step-content">
                        <strong>Scan QR code in Authenticator</strong>
                        <p>Use Google Authenticator, Microsoft Authenticator, 1Password, or Authy.</p>
                        
                        {setupQrCode && (
                          <div className="qr-code-wrapper">
                            <img src={setupQrCode} alt="2FA QR Code" className="qr-code-img" />
                          </div>
                        )}

                        <div className="manual-secret-box">
                          <span>Can't scan? Enter key manually:</span>
                          <div className="secret-copy-row">
                            <code>{setupSecret}</code>
                            <button type="button" onClick={handleCopySecret} className="btn-ghost btn-sm" title="Copy secret">
                              {copiedSecret ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="wizard-step" style={{ marginTop: '16px' }}>
                      <span className="step-number">2</span>
                      <div className="step-content">
                        <strong>Enter the 6-digit code</strong>
                        <p>Enter the temporary code generated by your app to verify setup.</p>
                        <input
                          type="text"
                          className="input-field twofa-code-input"
                          placeholder="123456"
                          maxLength={6}
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                          autoFocus
                          required
                        />
                      </div>
                    </div>

                    <div className="modal-actions" style={{ marginTop: '20px' }}>
                      <button type="button" className="btn-secondary" onClick={() => setSetupModalOpen(false)}>
                        Cancel
                      </button>
                      <button type="submit" className="btn-primary" disabled={confirming2FA || verificationCode.length < 6}>
                        {confirming2FA ? 'Verifying Code...' : 'Activate 2FA'}
                      </button>
                    </div>
                  </form>
                )}

                {/* Wizard: Step 3 (Recovery Codes Display) */}
                {newRecoveryCodes.length > 0 && (
                  <div className="recovery-codes-display">
                    <div className="recovery-alert-box">
                      <CheckCircle2 size={20} color="#10b981" style={{ flexShrink: 0 }} />
                      <div>
                        <strong>2FA is now active!</strong>
                        <p>Save your backup recovery codes. Each code can be used once if you ever lose your phone.</p>
                      </div>
                    </div>

                    <div className="recovery-grid-codes">
                      {newRecoveryCodes.map((code) => (
                        <div key={code} className="recovery-code-card">
                          <code>{code}</code>
                        </div>
                      ))}
                    </div>

                    <div className="recovery-download-actions">
                      <button type="button" className="btn-secondary" onClick={handleCopyCodes}>
                        {copiedCodes ? <Check size={14} color="#10b981" /> : <Copy size={14} />}
                        <span>{copiedCodes ? 'Copied to Clipboard' : 'Copy All Codes'}</span>
                      </button>
                      <button type="button" className="btn-secondary" onClick={handleDownloadCodes}>
                        <Download size={14} />
                        <span>Download (.txt)</span>
                      </button>
                    </div>

                    <div className="modal-actions" style={{ marginTop: '24px' }}>
                      <button 
                        type="button" 
                        className="btn-primary" 
                        style={{ width: '100%' }}
                        onClick={() => { setSetupModalOpen(false); setNewRecoveryCodes([]); }}
                      >
                        I Have Saved My Recovery Codes
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ─── Disable 2FA Confirmation Modal ─── */}
        {disableModalOpen && (
          <div className="modal-backdrop" onClick={() => setDisableModalOpen(false)}>
            <div className="modal-card modal-danger glass-panel" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-header-icon danger-icon">
                  <AlertTriangle size={20} />
                </div>
                <div>
                  <h3>Disable Two-Factor Authentication?</h3>
                  <p>This will reduce the security level of your vault.</p>
                </div>
                <button className="modal-close" onClick={() => setDisableModalOpen(false)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <div className="modal-body-content">
                <p>
                  Disabling 2FA removes the extra verification step when signing into CipherVault. Your account will only be protected by your master password.
                </p>
                <div className="delete-warning-box">
                  <AlertTriangle size={18} className="delete-warning-icon" />
                  <div>
                    <strong>Existing recovery codes will be revoked</strong>
                    <p>If you enable 2FA again in the future, you will need to scan a new QR code.</p>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setDisableModalOpen(false)} disabled={disabling2FA}>
                  Keep 2FA Enabled
                </button>
                <button type="button" className="btn-danger" onClick={handleDisable2FA} disabled={disabling2FA}>
                  {disabling2FA ? 'Disabling...' : 'Yes, Disable 2FA'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ─── Change Master Password Modal ─── */}
        {passwordModalOpen && (
          <div className="modal-backdrop" onClick={() => setPasswordModalOpen(false)}>
            <div className="modal-card glass-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '520px' }}>
              <div className="modal-header">
                <div className="modal-header-icon">
                  <Key size={20} />
                </div>
                <div>
                  <h3>Change Master Password</h3>
                  <p>Your master encryption key will be safely updated</p>
                </div>
                <button className="modal-close" onClick={() => setPasswordModalOpen(false)} aria-label="Close modal">
                  <X size={18} />
                </button>
              </div>

              <form onSubmit={handleChangeMasterPassword} className="admin-modal-form">
                {passwordError && (
                  <div className="admin-toast admin-toast-error">
                    <AlertTriangle size={16} />
                    <span>{passwordError}</span>
                  </div>
                )}

                {/* Current Password */}
                <div className="form-group">
                  <label htmlFor="current-master-pass">Current Master Password</label>
                  <div className="password-input-wrapper">
                    <input 
                      id="current-master-pass"
                      type={showCurrentPass ? 'text' : 'password'}
                      className="input-field"
                      placeholder="Enter your existing master password"
                      value={currentPassword}
                      onChange={(e) => setCurrentPassword(e.target.value)}
                      required
                    />
                    <button 
                      type="button" 
                      className="btn-toggle-eye" 
                      onClick={() => setShowCurrentPass(!showCurrentPass)}
                      aria-label="Toggle password visibility"
                    >
                      {showCurrentPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div className="form-group">
                  <label htmlFor="new-master-pass">New Master Password</label>
                  <div className="password-input-wrapper">
                    <input 
                      id="new-master-pass"
                      type={showNewPass ? 'text' : 'password'}
                      className="input-field"
                      placeholder="Choose a strong, unique password"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      required
                      minLength={8}
                    />
                    <button 
                      type="button" 
                      className="btn-toggle-eye" 
                      onClick={() => setShowNewPass(!showNewPass)}
                      aria-label="Toggle password visibility"
                    >
                      {showNewPass ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>

                  {/* Password Strength Indicator */}
                  {newPassword && (
                    <div className="password-strength-box">
                      <div className="strength-bar-track">
                        <div 
                          className="strength-bar-fill" 
                          style={{ 
                            width: `${(passwordStrength.score / 5) * 100}%`,
                            background: passwordStrength.color
                          }} 
                        />
                      </div>
                      <div className="strength-label-row">
                        <span>Strength: <strong style={{ color: passwordStrength.color }}>{passwordStrength.label}</strong></span>
                        <small>{newPassword.length} characters</small>
                      </div>
                    </div>
                  )}
                </div>

                {/* Confirm New Password */}
                <div className="form-group">
                  <label htmlFor="confirm-master-pass">Confirm New Master Password</label>
                  <input 
                    id="confirm-master-pass"
                    type="password"
                    className="input-field"
                    placeholder="Repeat your new master password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                  />
                  {confirmPassword && newPassword !== confirmPassword && (
                    <small style={{ color: 'var(--danger)', marginTop: '4px', display: 'block' }}>
                      Passwords do not match.
                    </small>
                  )}
                </div>

                <div className="notice-box-info">
                  <Lock size={16} style={{ flexShrink: 0, marginTop: '2px' }} />
                  <p>
                    Your vault key will be decrypted in memory with your current password and re-wrapped with your new password before sending to the server.
                  </p>
                </div>

                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="btn-secondary" 
                    onClick={() => setPasswordModalOpen(false)}
                    disabled={changingPass}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="btn-primary" 
                    disabled={changingPass || !currentPassword || !newPassword || newPassword !== confirmPassword}
                  >
                    {changingPass ? 'Re-encrypting Vault...' : 'Save New Master Password'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
